import {
    Box,
    Heading,
    Text,
    VStack,
    HStack,
    Flex,
    Divider,
    Badge,
    Button,
    Collapse,
    Icon,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
} from "@chakra-ui/react";
import {
    FaCar,
} from "react-icons/fa";
import {
    MdAirlineSeatReclineExtra,
} from "react-icons/md";
import {
    BsFuelPumpFill,
} from "react-icons/bs";
import {
    TbAutomaticGearbox,
} from "react-icons/tb";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { useState } from "react";
import { capitalize } from "lodash";
import { useTranslation } from "react-i18next";
import Amenity from "../accommodation/Amenity";
import { formatDateShortCET, labelTimeCET } from "../../utils/helpers";

const AccommodationBookingSummary = ({ accommodation }) => (
    <Box border="1px" borderColor="gray.200" borderRadius="lg" p={4} width="full">
        <VStack align="flex-start" spacing={2}>
            <Text fontSize="sm" color="gray.500">{accommodation.type}</Text>
            <Heading as="h3" size="sm">{accommodation.name}</Heading>
            <Text fontSize="sm">{`${accommodation.address}, ${accommodation.city}`}</Text>
            <Badge colorScheme="green">{accommodation.rating}</Badge>
            <HStack spacing={2} wrap="wrap">
                {accommodation.amenities && Object.keys(accommodation.amenities).map((key, i) => (
                    <Amenity key={i} amenityKey={key} size="sm" />
                ))}
            </HStack>
        </VStack>
    </Box>
);

const CarBookingSummary = ({ car, t }) => (
    <Box border="1px" borderColor="gray.200" borderRadius="lg" p={4} width="full">
        <VStack align="flex-start" spacing={2}>
            <Heading as="h3" size="sm">{car.name}</Heading>
            <Flex wrap="wrap" fontSize="sm" gap={3}>
                <HStack><Icon as={FaCar} /><Text>{capitalize(car.vehicleType)}</Text></HStack>
                <HStack><Icon as={TbAutomaticGearbox} /><Text>{car.automatic ? t("bookingAutomatic") : t("bookingManual")}</Text></HStack>
                <HStack><Icon as={MdAirlineSeatReclineExtra} /><Text>{car.numberOfSeats} {t("bookingSeats")}</Text></HStack>
                <HStack><Icon as={BsFuelPumpFill} /><Text>{capitalize(car.fuelType)}</Text></HStack>
            </Flex>
        </VStack>
    </Box>
);

const TourBookingSummary = ({ tour, t }) => (
    <Box border="1px" borderColor="gray.200" borderRadius="lg" p={4} width="full">
        <VStack align="flex-start" spacing={2}>
            <Text fontSize="sm" color="gray.500">{t("bookingTour")}</Text>
            <Heading as="h3" size="sm">{tour.name}</Heading>
            <Text fontSize="sm">{t("reservationCities")} {tour.cities.join(", ")}</Text>
            <Text fontSize="sm">{t("reservationPickupLocation")} {tour.pickupLocation}</Text>
        </VStack>
    </Box>
);

const statusTranslationKey = {
    approved: "bookingStatusApproved",
    pending: "bookingStatusPending",
    declined: "bookingStatusDeclined",
    draft: "bookingStatusDraft",
};

const statusColorScheme = {
    approved: "green",
    pending: "orange",
    declined: "red",
    draft: "gray",
};

function ReservationDetails({ bookingSummary, hidePaymentSummary = false }) {
    const { t } = useTranslation("common");
    const [showUnits, setShowUnits] = useState(false);
    const {
        referenceNumber, currentStatus, bookingType, checkInDate, checkOutDate,
        totalStayLengthInDays, adults, children, promoCode, fullPrice, discountPrice,
        deposit, pickupTime, dropoffTime, accommodation, accommodationUnits,
        car, tour, isAffiliateReservation, affiliatePrice, commission
    } = bookingSummary;

    const toggleUnits = () => setShowUnits((prev) => !prev);

    const calculatedRemaining = (
        parseFloat(discountPrice.replace(/[^0-9.-]+/g, "")) -
        parseFloat(deposit.replace(/[^0-9.-]+/g, ""))
    ).toFixed(2);

    const statusLabel = statusTranslationKey[currentStatus]
        ? t(statusTranslationKey[currentStatus])
        : currentStatus;

    const isDeclined = currentStatus === "declined";
    const statusBadgeColor = statusColorScheme[currentStatus] || "gray";

    return (
        <VStack spacing={6} align="stretch" maxW="900px" mx="auto" py={6}>
            <Heading as="h2" size="lg" textAlign="center" color="teal.600">
                {t("reservationSummaryTitle")}
            </Heading>

            {bookingType === "stay" && accommodation && (
                <AccommodationBookingSummary accommodation={accommodation} />
            )}
            {bookingType === "car" && car && <CarBookingSummary car={car} t={t} />}
            {bookingType === "tour" && tour && <TourBookingSummary tour={tour} t={t} />}

            {isAffiliateReservation && (
              <Box border="1px" borderColor="teal.200" borderRadius="lg" p={4} bg="teal.50">
                <VStack align="flex-start" spacing={3}>
                  <Heading as="h3" size="sm" color="teal.700">
                    {t("reservationPriceSummary")}
                  </Heading>
                  <Flex justify="space-between" w="full">
                    <Text fontSize="sm">{t("affiliateBasePrice")}</Text>
                    <Text fontSize="sm" fontWeight="bold">{fullPrice}</Text>
                  </Flex>
                  <Flex justify="space-between" w="full">
                    <Text fontSize="sm">{t("reservationAffiliatePrice")}</Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {affiliatePrice != null
                        ? `EUR ${affiliatePrice}`
                        : t("affiliateNotAvailable")}
                    </Text>
                  </Flex>
                  {commission != null && (
                    <Flex justify="space-between" w="full">
                      <Text fontSize="sm">{t("bookingCommission")}</Text>
                      <Text fontSize="sm" fontWeight="bold" color="teal.700">
                        EUR {commission}
                      </Text>
                    </Flex>
                  )}
                </VStack>
              </Box>
            )}

            <Box border="1px" borderColor="gray.200" borderRadius="lg" p={4}>
                <VStack align="flex-start" spacing={3}>
                    <Heading as="h3" size="sm">{t("bookingReservationDetails")}</Heading>
                    <Text fontSize="sm">
                        <strong>{t("reservationStatus")}</strong>{" "}
                        <Badge colorScheme={statusBadgeColor}>
                            {statusLabel}
                        </Badge>
                    </Text>
                    <Text fontSize="sm"><strong>{t("reservationBookingId")}</strong> {referenceNumber}</Text>

                    <Flex justify="space-between" w="full" wrap="wrap">
                        <Box>
                            <Text fontSize="sm">
                                <strong>{bookingType === "car" ? t("bookingPickUp") : t("bookingCheckIn")}</strong>
                            </Text>
                            <Text fontWeight="bold">
                                {formatDateShortCET(checkInDate)}
                                {pickupTime ? `, ${labelTimeCET(pickupTime)}` : `, ${labelTimeCET("14:00")}`}
                            </Text>
                        </Box>
                        <Divider orientation="vertical" h="40px" />
                        <Box>
                            <Text fontSize="sm">
                                <strong>{bookingType === "car" ? t("bookingDropOff") : t("bookingCheckOut")}</strong>
                            </Text>
                            <Text fontWeight="bold">
                                {formatDateShortCET(checkOutDate)}
                                {dropoffTime ? `, ${labelTimeCET(dropoffTime)}` : `, ${labelTimeCET("10:00")}`}
                            </Text>
                        </Box>
                    </Flex>

                    <Text fontSize="sm">
                        <strong>{bookingType === "car" ? t("bookingTotalLengthRent") : t("bookingTotalLengthStay")}</strong>{" "}
                        {totalStayLengthInDays}{" "}
                        {bookingType === "stay" ? t("bookingNights") : t("bookingDays")}
                    </Text>
                    <Text fontSize="sm">
                        <strong>{t("reservationGuests")}</strong>{" "}
                        {t("reservationGuestsCount", { adults, children: children || 0 })}
                    </Text>
                    <Text fontSize="sm">
                        <strong>{t("reservationPromoCode")}</strong>{" "}
                        {promoCode?.code || t("reservationPromoCodeNone")}
                    </Text>

                    {bookingType === "stay" && accommodationUnits?.length > 0 && (
                        <>
                            <Button
                                size="sm"
                                variant="ghost"
                                rightIcon={showUnits ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                onClick={toggleUnits}
                            >
                                {t("reservationViewSelectedUnits")}
                            </Button>
                            <Collapse in={showUnits} animateOpacity>
                                <VStack align="flex-start" spacing={1}>
                                    {accommodationUnits.map((unit, i) => (
                                        <Text fontSize="sm" key={i}>
                                            {unit.quantity}x {unit.name} ({formatDateShortCET(unit.startDate)} - {formatDateShortCET(unit.endDate)})
                                        </Text>
                                    ))}
                                </VStack>
                            </Collapse>
                        </>
                    )}
                </VStack>
            </Box>

            {!hidePaymentSummary && (
            <Box
                border="1px"
                borderColor={isDeclined ? "red.200" : "gray.200"}
                borderRadius="lg"
                p={4}
                bg={isDeclined ? "red.50" : "white"}
            >
                <VStack align="flex-start" spacing={3}>
                    <Heading as="h3" size="sm">{t("reservationPriceSummary")}</Heading>

                    {isDeclined && (
                        <Alert
                            status="warning"
                            variant="left-accent"
                            borderRadius="md"
                            w="full"
                            alignItems="flex-start"
                        >
                            <AlertIcon mt={0.5} />
                            <Box>
                                <AlertTitle fontSize="sm" mb={1}>
                                    {t("reservationDeclinedPaymentTitle")}
                                </AlertTitle>
                                <AlertDescription fontSize="sm">
                                    {t("reservationDeclinedPaymentDescription")}
                                </AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    <Flex justify="space-between" w="full">
                        <Text fontSize="sm">{t("reservationFullPrice")}</Text>
                        <Text fontSize="sm" textDecoration={fullPrice !== discountPrice ? "line-through" : "none"}>{fullPrice}</Text>
                    </Flex>
                    {fullPrice !== discountPrice && (
                        <Flex justify="space-between" w="full">
                            <Text fontSize="sm" color="red.500">{t("reservationDiscountedPrice")}</Text>
                            <Text fontSize="sm" color="red.500">{discountPrice}</Text>
                        </Flex>
                    )}
                    <Flex justify="space-between" w="full" align="center">
                        <HStack spacing={2} flexWrap="wrap">
                            <Text
                                fontSize="sm"
                                color={isDeclined ? "gray.500" : undefined}
                            >
                                {isDeclined
                                    ? t("reservationDepositAuthorized")
                                    : t("reservationDepositPaid")}
                            </Text>
                            {isDeclined && (
                                <Badge colorScheme="red" variant="subtle" fontSize="xs">
                                    {t("reservationDepositNotCaptured")}
                                </Badge>
                            )}
                        </HStack>
                        <Text
                            fontSize="sm"
                            textDecoration={isDeclined ? "line-through" : "none"}
                            color={isDeclined ? "gray.400" : undefined}
                            fontWeight={isDeclined ? "normal" : undefined}
                        >
                            {deposit}
                        </Text>
                    </Flex>
                    <Flex justify="space-between" w="full" align="center">
                        <Text
                            fontWeight="bold"
                            color={isDeclined ? "gray.500" : undefined}
                        >
                            {t("reservationRemainingToPay")}
                        </Text>
                        {isDeclined ? (
                            <Text fontSize="sm" color="gray.500" fontStyle="italic">
                                {t("reservationRemainingNotApplicable")}
                            </Text>
                        ) : (
                            <Text fontWeight="bold">{calculatedRemaining}</Text>
                        )}
                    </Flex>
                </VStack>
            </Box>
            )}
        </VStack>
    );
}

export default ReservationDetails;
